import "leaflet/dist/leaflet.css";
import React, { useCallback, useEffect, useState } from "react";
import { MapContainer, ZoomControl } from "react-leaflet";
import { useMapSas } from "../../../hooks/useMapSas";
import { IAirportMap } from "../../../models/airports/AirportConfig";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppSelector } from "../../../store/StoreHooks";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import { MapErrorId, mapApiUrl } from "../../../utils/maps/MapConstants";
import { getAzureMapTileUrl } from "../../../utils/maps/MapHelperFunctions";
import DisplayMapErrors from "../DisplayMapErrors";
import AirportMarkers from "../map-markers/AirportMarkers";
import TileLayerWithHeader from "../TileLayerWithHeader";
import { mapStyles } from "./mapStyles";

export interface IMapDisplayAirports {
  airportMapDetails: IAirportMap[];
}

const MapDisplayAirports: React.FC<IMapDisplayAirports> = ({
  airportMapDetails = [],
}) => {
  const classes = mapStyles();

  const airport = useAppSelector((state) => getAirportCode(state));
  const airportConfig = getAirportConfig(airport);
  const {
    sas: mapSas,
    error: mapSasError,
    forceRefresh: refreshMapSas,
    needsReset: mapSasNeedsReset,
    acknowledgeReset: acknowledgeMapSasReset,
  } = useMapSas(airport);
  const azureMapUrl = getAzureMapTileUrl(airport, mapApiUrl, "BASE", mapSas);

  const [mapApiFailures, setMapApiFailures] = useState<MapErrorId[]>([]);
  const [tileLayerKey, setTileLayerKey] = useState(0);

  const airportMarkersContent = airportMapDetails.map((airport) => (
    <AirportMarkers
      key={airport.id}
      code={airport.code}
      name={airport.name}
      position={airport.location}
    />
  ));

  const handleMapApiFailure = useCallback(
    (errorID: MapErrorId, isReset = false) => {
      if (isReset) {
        setMapApiFailures((prev) => prev.filter((id) => id !== errorID));
        return;
      }

      setMapApiFailures((prevFailures) =>
        prevFailures.includes(errorID)
          ? prevFailures
          : [...prevFailures, errorID]
      );
    },
    [setMapApiFailures]
  );

  useEffect(() => {
    handleMapApiFailure("SAS", !mapSasError);
  }, [mapSasError, handleMapApiFailure]);

  useEffect(() => {
    if (mapSasNeedsReset) {
      setTileLayerKey((prev) => prev + 1);
      acknowledgeMapSasReset();
    }
  }, [mapSasNeedsReset, acknowledgeMapSasReset]);

  return (
    <div className={classes.mapContainer} data-testid="multi-airport-map">
      <MapContainer
        center={airportConfig.location as [number, number]}
        zoom={7}
        style={{ height: "100%", width: "100%", borderRadius: "8px" }}
        zoomControl={false}
        minZoom={4}
      >
        <ZoomControl position="bottomright" />
        {mapSas && (
          <TileLayerWithHeader
            key={tileLayerKey}
            id="BASE"
            url={azureMapUrl}
            attribution="© Microsoft Azure Maps"
            setMapApiFailures={handleMapApiFailure}
            onAuthError={refreshMapSas}
          />
        )}
        {airportMarkersContent}
        <DisplayMapErrors messages={mapApiFailures} />
      </MapContainer>
    </div>
  );
};

export default MapDisplayAirports;
